import {
  Component,
  ChangeDetectionStrategy,
  inject,
  signal,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser, NgClass } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import { ArticleDetailData } from '@models/article-detail';
import { AssetFormatterPipe } from '@pipes/asset-formatter-pipe';
import { Carousel } from '@components/carousel/carousel';
import { FormComponent } from '@classes/form';
import { ShareFloat } from '@components/share-float/share-float';
import { SpinnerComponent } from '@components/spinner/spinner.component';

@Component({
  selector: 'app-article-detail',
  imports: [
    RouterLink,
    AssetFormatterPipe,
    Carousel,
    SpinnerComponent,
    NgClass,
    ShareFloat,
  ],
  templateUrl: './article-detail.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './article-detail.scss',
})
export class ArticleDetail extends FormComponent {
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private utterance: SpeechSynthesisUtterance | null = null;
  private keepAliveInterval: ReturnType<typeof setInterval> | null = null;

  private router = inject(Router);
  private sanitizer = inject(DomSanitizer);

  override data = signal<ArticleDetailData>(new ArticleDetailData());
  iframe = signal<SafeHtml>('');
  playing = signal(false);

  constructor() {
    super();

    if (this.isBrowser && 'speechSynthesis' in window) {
      window.speechSynthesis.onvoiceschanged = () => {
        // this.voicesReady = true;
      };
      // Fuerza la carga inicial en algunos Chrome
      window.speechSynthesis.getVoices();
    }
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      const slug = params.get('slug') ?? '';

      this.restart();

      if (slug) {
        this.loadEndpoint = `api/article/${slug}`;

        this.load();
      } else {
        this.router.navigate(['/404'], { replaceUrl: true });
      }
    });
  }

  restart() {
    this.initialized.set(false);
    this.startLoading();
    this.data.set(new ArticleDetailData());
  }

  override onLoadSuccess(response: any): void {
    this.data.set(response.data);

    if (this.data().article?.podcast_js_src) {
      this.iframe.set(
        this.sanitizer.bypassSecurityTrustHtml(
          this.data()!.article!.podcast_js_src!,
        ),
      );
    }

    super.onLoadSuccess(response);
  }

  override onLoadError(error: any): void {
    this.router.navigate(['/404'], { replaceUrl: true });

    console.error(error);
  }

  togglePlay() {
    if (this.playing()) {
      this.stop();
    } else {
      this.speak(this.stripHtml(this.data().article!.content));
    }
  }

  speak(text: string, lang = 'es-MX') {
    if (!this.isBrowser || !('speechSynthesis' in window)) return;

    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = lang;
    utterance.rate = 1;
    utterance.pitch = 1;

    utterance.onend = () => {
      this.playing.set(false);
      this.clearKeepAlive();
      this.utterance = null;
    };

    utterance.onerror = () => {
      this.playing.set(false);
      this.clearKeepAlive();
      this.utterance = null;
    };

    // Referencia en la clase: evita que Chrome lo recolecte a mitad de la lectura
    this.utterance = utterance;
    this.playing.set(true);

    window.speechSynthesis.speak(utterance);
    this.startKeepAlive();
  }

  stop() {
    if (this.isBrowser) window.speechSynthesis.cancel();
    this.playing.set(false);
    this.clearKeepAlive();
    this.utterance = null;
  }

  private startKeepAlive() {
    this.clearKeepAlive();
    // Workaround para el bug de Chrome que corta la lectura a los ~15s
    this.keepAliveInterval = setInterval(() => {
      if (!window.speechSynthesis.speaking) {
        this.clearKeepAlive();
        return;
      }
      window.speechSynthesis.pause();
      window.speechSynthesis.resume();
    }, 14000);
  }

  private clearKeepAlive() {
    if (this.keepAliveInterval) {
      clearInterval(this.keepAliveInterval);
      this.keepAliveInterval = null;
    }
  }

  private stripHtml(html: string): string {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent ?? '';
  }
}
